export type OrderStatus =
  | 'new'
  | 'accepted'
  | 'under_review'
  | 'in_progress'
  | 'ready_for_delivery'
  | 'completed'
  | 'cancelled';

export type OrderPriority = 'normal' | 'urgent';

/**
 * The four approved pickup & delivery methods (FB06).
 *  - self_dropoff_driver_delivery: customer drops at the laundry, a driver delivers.
 *  - self_dropoff_self_pickup:     customer drops at and picks up from the laundry.
 *  - driver_pickup_self_pickup:    a driver picks up, the customer picks up from the laundry.
 *  - driver_pickup_driver_delivery: a driver picks up and delivers back.
 */
export type Fulfillment =
  | 'self_dropoff_driver_delivery'
  | 'self_dropoff_self_pickup'
  | 'driver_pickup_self_pickup'
  | 'driver_pickup_driver_delivery';

/** The four methods in display order. */
export const FULFILLMENTS: Fulfillment[] = [
  'self_dropoff_driver_delivery',
  'self_dropoff_self_pickup',
  'driver_pickup_self_pickup',
  'driver_pickup_driver_delivery',
];

/** Methods where a driver collects the clothes from the customer. */
export const PICKUP_DRIVER_METHODS: Fulfillment[] = [
  'driver_pickup_self_pickup',
  'driver_pickup_driver_delivery',
];

/** Methods where a driver delivers the order back to the customer. */
export const DELIVERY_DRIVER_METHODS: Fulfillment[] = [
  'self_dropoff_driver_delivery',
  'driver_pickup_driver_delivery',
];

export type AccountStatus = 'approved' | 'pending' | 'rejected';

export interface Account {
  id: string;
  laundryName: string;
  email: string;
  phone: string;
  /** Demo-only credential; never persisted to browser storage. */
  password: string;
  status: AccountStatus;
  rejectionReason?: string;
  cityId: string;
  district: string;
  logoUrl?: string;
}

export interface DeliveryInfo {
  /** Order reached the delivery stage. */
  inDeliveryStage: boolean;
  /** A delivery driver is currently available. */
  driverAvailable: boolean;
  /** Customer's choice while waiting. */
  customerChoice: 'wait' | 'cancel';
  /** Minutes the customer has been waiting in the delivery stage. */
  waitingMinutes: number;
  /** A driver already accepted the task. */
  driverAccepted: boolean;
}

export interface OrderItem {
  id: string;
  /** Reference to a catalogue item (LaundryItem.id). */
  itemId: string;
  /** Snapshot of the item name at order time. */
  itemName: string;
  serviceType: ItemServiceType;
  quantity: number;
}

export interface OrderRecord {
  id: string;
  customerName: string;
  customerPhone: string;
  priority: OrderPriority;
  fulfillment: Fulfillment;
  /** Final amount shown everywhere = items subtotal (approved prices) + deliveryFee. */
  total: number;
  /** Delivery fee recorded for the customer; 0 when no driver delivery is needed. */
  deliveryFee: number;
  status: OrderStatus;
  /** 0 = today, 1 = yesterday … used by the service layer to build real dates. */
  createdAtDaysAgo: number;
  /** null unless the order is completed. 0 = completed today. */
  completedDaysAgo: number | null;
  /**
   * Extra minutes to shift the creation time within the day. Keeps the demo
   * order/notification ordering coherent (a notification always lands after the
   * order it refers to). Optional — defaults to 0.
   */
  createdAtMinutesAgo?: number;
  assignedDriver: string | null;
  delivery: DeliveryInfo | null;
  /** Recorded line items requested by the customer. */
  items: OrderItem[];
  /** True once the laundry adopted the contents review (FB06). */
  reviewAdopted?: boolean;
  /** Set when the order is cancelled (by the customer or rejected by the laundry). */
  cancelReason?: CancelReason | null;
  /** Refund status reported by the system, when applicable. */
  refundStatus?: RefundStatus | null;
  /** Smart-box usage reported by the system, when applicable. */
  smartBox?: SmartBoxInfo | null;
}

export interface OrderView
  extends Omit<OrderRecord, 'createdAtDaysAgo' | 'completedDaysAgo'> {
  createdAt: string;
  completedAt: string | null;
  needsManualAssignment: boolean;
  escalationReason?: string;
}

/** One line in the received-contents review (FB06). */
export interface ReviewLine {
  key: string;
  /** Original order item id, or null for an added (unrecorded) item. */
  orderItemId: string | null;
  itemId: string;
  itemName: string;
  serviceType: ItemServiceType;
  quantity: number;
  source: 'recorded' | 'added';
}

export interface ReviewPayload {
  lines: ReviewLine[];
}

export interface DashboardSummary {
  newOrders: number;
  inProgress: number;
  completedToday: number;
  revenueToday: number;
  currency: string;
}

export interface CityOption {
  id: string;
  name: string;
  districts: string[];
}

export interface SystemSettings {
  currency: string;
  currencyCode: string;
  otpValidityMinutes: number;
  manualAssignmentGraceMinutes: number;
  upload: {
    accept: string;
    maxSizeMB: number;
  };
  /** Admin-controlled rules for item images. */
  imageUpload: {
    accept: string;
    maxSizeMB: number;
  };
  /** Admin-controlled execution durations (FB10). null = not defined yet. */
  durations: ItemDurationSettings;
  /** System finance settings (FB07). */
  finance: {
    commissionRate: number;
  };
  /** Admin-defined password policy (FB10). */
  password: {
    minLength: number;
  };
  cities: CityOption[];
  additionalDocuments: string[];
}

export interface RegisterPayload {
  laundryName: string;
  description: string;
  phone: string;
  email: string;
  password: string;
  cityId: string;
  district: string;
  street: string;
  building: string;
  coverageDistricts: string[];
  fromTime: string;
  toTime: string;
  documents: Record<string, string>;
}

/* ------------------------------------------------------------------ *
 * Items & services (FB03)
 * ------------------------------------------------------------------ */

export type ItemServiceType = 'wash_only' | 'iron_only' | 'wash_iron';

/** The three service types in display order. */
export const ITEM_SERVICE_TYPES: ItemServiceType[] = ['wash_only', 'iron_only', 'wash_iron'];

/** Availability + prices for one service type on a single item. */
export interface ItemServiceRate {
  available: boolean;
  normal: number | null;
  urgent: number | null;
}

export interface LaundryItem {
  id: string;
  name: string;
  imageUrl: string | null;
  active: boolean;
  services: Record<ItemServiceType, ItemServiceRate>;
}

/** Payload used when creating or editing an item. */
export interface ItemPayload {
  name: string;
  imageUrl: string | null;
  services: Record<ItemServiceType, ItemServiceRate>;
}

/**
 * Global execution durations (FB10). These belong to the laundry as a whole,
 * not per item or per service type.
 */
export interface ItemDurationSettings {
  normalHours: number | null;
  urgentHours: number | null;
}

/* ------------------------------------------------------------------ *
 * Sub-drivers (FB04)
 * ------------------------------------------------------------------ */

export type DriverAvailability = 'available' | 'busy' | 'unavailable';

export type DriverTaskType = 'pickup' | 'delivery';

/** The three task types in display order. */
export const DRIVER_TASK_TYPES: DriverTaskType[] = ['pickup', 'delivery'];

/** A stored document slot (national id / license / vehicle). */
export interface DriverDocument {
  fileName: string;
  /** Demo placeholder URL only; null when no file is attached. */
  url: string | null;
}

export interface DriverDocuments {
  nationalId: DriverDocument | null;
  license: DriverDocument | null;
  vehicle: DriverDocument | null;
}

export type DriverDocumentKey = keyof DriverDocuments;

/** The three document slots in display order. */
export const DRIVER_DOCUMENT_KEYS: DriverDocumentKey[] = ['nationalId', 'license', 'vehicle'];

export interface Driver {
  id: string;
  fullName: string;
  phone: string;
  username: string;
  avatarUrl: string | null;
  availability: DriverAvailability;
  /** Temporary suspension, independent from availability. */
  accountDisabled: boolean;
  documents: DriverDocuments;
}

/** Editable profile data (not login credentials). */
export interface DriverProfilePayload {
  fullName: string;
  phone: string;
  avatarUrl: string | null;
  documents: DriverDocuments;
}

/** Payload used when the laundry creates a sub-driver account. */
export interface CreateDriverPayload extends DriverProfilePayload {
  username: string;
  password: string;
}

/** One completed pickup/delivery task (read-only history row). */
export interface DriverTask {
  id: string;
  driverId: string;
  type: DriverTaskType;
  orderId: string;
  /** ISO date-time of completion. */
  completedAt: string;
  deliveryFee: number;
}

export type CancelReason = 'customer_cancelled' | 'laundry_rejected';

export type RefundStatus = 'pending' | 'processing' | 'completed';

export interface SmartBoxInfo {
  stage: 'pickup' | 'delivery';
  /** Real description coming from the system. */
  description: string;
}

/* ------------------------------------------------------------------ *
 * Finance & settlements (FB07)
 * ------------------------------------------------------------------ */

export type SettlementStatus = 'processing' | 'transferred' | 'rejected';

/**
 * Origin of the delivery fee recorded for finance:
 *  - sub_driver: task executed by a laundry sub-driver → fee transferred to the laundry.
 *  - independent: task executed by an independent driver → fee NOT transferred.
 *  - none: the order needs no driver task.
 */
export type DeliveryFeeSource = 'sub_driver' | 'independent' | 'none';

/** Per-order finance breakdown (read-only, system-computed). */
export interface OrderFinanceView {
  orderId: string;
  /** Recognition date built by the service from the record. */
  recognizedAt: string;
  /** Final order value after the contents review was adopted. */
  finalTotal: number;
  /** Admin/system commission ratio (0.15 = 15%). */
  commissionRate: number;
  commissionAmount: number;
  /** Delivery fee transferred to the laundry (0 when executed by an independent driver). */
  deliveryFeeToLaundry: number;
  deliveryFeeSource: DeliveryFeeSource;
  /** Net amount due to the laundry (final total − commission + transferred fee). */
  netPayable: number;
  /** Customer debt recorded by the system; never reduces netPayable. */
  customerDebt: number;
}

export interface FinanceSummary {
  directRevenue: number;
  transferredDeliveryFees: number;
  totalRevenue: number;
  currency: string;
}

export type OutstandingComponentType = 'order' | 'package';

export interface OutstandingComponent {
  id: string;
  type: OutstandingComponentType;
  label: string;
  amount: number;
  orderId?: string;
}

export interface OutstandingDues {
  total: number;
  currency: string;
  components: OutstandingComponent[];
  /** True when package entitlement exists but its finance source is not confirmed. */
  packagePending: boolean;
}

export interface SettlementRequest {
  id: string;
  requestedAt: string;
  processedAt: string | null;
  amount: number;
  status: SettlementStatus;
  rejectionReason: string | null;
  /** Transfer proof image, set once an administration transfer has a receipt. */
  transferReceiptUrl: string | null;
}

/* ------------------------------------------------------------------ *
 * Reviews (FB08)
 * ------------------------------------------------------------------ */

/**
 * A single read-only customer review.
 * targetId is 'laundry' for the laundry's own reviews, otherwise the
 * related Driver id. Reviews are display-only — the laundry never replies,
 * edits or hides them; abusive handling belongs to the admin.
 */
export interface Review {
  id: string;
  targetId: string;
  customerName: string;
  /** 1–5 stars as submitted by the customer. */
  rating: number;
  /** The note exactly as submitted, or null when the customer left none. */
  comment: string | null;
  /** ISO date-time of the review. */
  createdAt: string;
  /** Related order id, shown as a link to the existing order detail. */
  orderId: string;
}

/**
 * A review seed as stored in the demo data. The real creation date is derived by
 * the service from `createdMinutesAgo`, so demo times are always in the past.
 */
export interface ReviewSeed {
  id: string;
  targetId: string;
  customerName: string;
  rating: number;
  comment: string | null;
  createdMinutesAgo: number;
  orderId: string;
}

/** Average stars + review count, computed from the full dataset. */
export interface RatingSummary {
  /** null when there are no reviews at all — never a default average. */
  average: number | null;
  count: number;
}

/* ------------------------------------------------------------------ *
 * Notifications (FB09)
 * ------------------------------------------------------------------ */

/**
 * The approved notification kinds. The laundry only receives these; it never
 * sends notifications and there is no separate messaging centre.
 */
export type NotificationKind =
  | 'new_order'
  | 'order_handoff'
  | 'manual_assignment'
  | 'new_review'
  | 'package_subscription';

/** A stored notification as returned by the system (raw record). */
export interface NotificationRecord {
  id: string;
  kind: NotificationKind;
  /** ISO date-time coming from the system. */
  createdAt: string;
  /** Related order id, when the notification is order-related. */
  orderId?: string | null;
  /** Related review id, when the notification is a review. */
  reviewId?: string | null;
  /** Customer name, only when the system returns it for this notification. */
  customerName?: string | null;
  /** Rating value, only when the system returns it for this notification. */
  rating?: number | null;
  /** Package name, only when the system returns it for this notification. */
  packageName?: string | null;
}

/**
 * A notification seed as stored in the demo data. The real creation date is
 * derived by the service from `createdMinutesAgo`, so demo times are always in
 * the past and never before the element they reference.
 */
export interface NotificationSeed {
  id: string;
  kind: NotificationKind;
  createdMinutesAgo: number;
  orderId?: string | null;
  reviewId?: string | null;
  customerName?: string | null;
  rating?: number | null;
  packageName?: string | null;
}

/**
 * A notification resolved for display. Values such as customer name / rating are
 * pulled from the same orders & reviews datasets used by the other pages, so the
 * notification never contradicts them. `to` is the only approved navigation
 * destination (null when there is none or the related element is unreachable).
 */
export interface NotificationView {
  id: string;
  kind: NotificationKind;
  createdAt: string;
  orderId: string | null;
  customerName: string | null;
  rating: number | null;
  packageName: string | null;
  /** Route to open the related element, or null. */
  to: string | null;
  /** True when an order-linked notification points to a missing order. */
  targetMissing: boolean;
  /** True when the laundry has flagged this notification as handled. */
  processed: boolean;
}

/**
 * Live operational alerts surfaced as transient toasts on the dashboard.
 * These are attention events (an order that needs a driver, a delivery problem)
 * that briefly appear and auto-dismiss — not part of the notifications log.
 */
export type LiveAlertKind = 'assignment_needed' | 'driver_issue' | 'delay' | 'new_order';

/* ------------------------------------------------------------------ *
 * Settings (FB10)
 * ------------------------------------------------------------------ */

/**
 * Status of the laundry's data-modification request:
 *  - none:        no request — the approved data is simply displayed.
 *  - under_review: sent and awaiting an admin decision.
 *  - approved:     admin approved; the approved data was updated from the system.
 *  - rejected:     admin rejected; the previous data stays in effect.
 */
export type LaundryModificationStatus = 'none' | 'under_review' | 'approved' | 'rejected';

export interface LaundryAddress {
  cityId: string;
  district: string;
  street: string;
  building: string;
}

/** The laundry's approved profile data (FB10). */
export interface LaundryProfile {
  name: string;
  description: string;
  logoUrl: string | null;
  phone: string;
  address: LaundryAddress;
  /** Districts the laundry covers (chosen from the admin geography). */
  coverageDistricts: string[];
  fromTime: string;
  toTime: string;
  /** Global execution durations; null = not defined by the admin yet. */
  normalHours: number | null;
  urgentHours: number | null;
}

/**
 * A data-modification request. `previous` is the approved data at submission
 * time and `proposed` is what the laundry asked for — the laundry can never
 * apply it directly; only the admin decision (the system) does.
 */
export interface LaundryModificationRequest {
  id: string;
  status: Exclude<LaundryModificationStatus, 'none'>;
  submittedAt: string;
  rejectionReason: string | null;
  previous: LaundryProfile;
  proposed: LaundryProfile;
}

/** Operational districts available for the laundry's own city (admin geography). */
export interface LaundryCoverageOptions {
  cityId: string;
  cityName: string;
  districts: string[];
}

export interface SettingsData {
  /** Approved data currently in effect. */
  profile: LaundryProfile;
  /** Latest modification request, or null. */
  modification: LaundryModificationRequest | null;
  coverage: LaundryCoverageOptions;
}